/** Detached session records. Blob bytes live in the account-scoped asset store. */
export const SESSION_RECORD_VERSION = 1;

export function hasCreativeWork(snapshot) {
  return Boolean(snapshot.session?.wizardDraft?.trim() || snapshot.session?.creativeBrief || snapshot.session?.branches?.length ||
    snapshot.references?.assets?.length || snapshot.references?.guideImage || snapshot.generationRun?.request);
}

export function createSessionRecord(snapshot, now = Date.now()) {
  const { accountId, session } = snapshot;
  if (!accountId || !session?.id) throw new Error('Sign in before saving this session.');
  const assets = [];
  const seenBlobs = new WeakMap();
  function detach(value, path) {
    if (value instanceof Blob) {
      let id = seenBlobs.get(value);
      if (!id) {
        id = path;
        seenBlobs.set(value, id);
        assets.push({ accountId, sessionId: session.id, id, blob: value,
          name: value.name || null, mimeType: value.type, size: value.size });
      }
      return { $vibeAsset: id };
    }
    if (Array.isArray(value)) return value.map((item, index) => detach(item, `${path}.${index}`));
    if (value && Object.getPrototypeOf(value) === Object.prototype) {
      return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, detach(item, `${path}.${key}`)]));
    }
    // An object URL belongs to this page; hydration creates a new one from its blob.
    if (typeof value === 'string' && value.startsWith('blob:')) return null;
    return value;
  }
  const run = snapshot.generationRun;
  const generationRun = run ? { request: run.request, attemptId: run.attemptId,
    attemptSlotIds: run.attemptSlotIds, status: run.status, results: run.results } : run;
  const data = detach({ session, promptStructure: snapshot.promptStructure,
    settings: snapshot.settings, references: snapshot.references, generationRun }, 'session');
  if (session.finalImageUrl) {
    const final = (session.branches || []).flatMap((branch) => branch.iterations || [])
      .flatMap((iteration) => iteration.batches || []).flat().find((image) => image?.url === session.finalImageUrl);
    if (final?.blob) data.session.finalImageBlob = detach(final.blob, 'session.finalImageBlob');
  }
  return {
    record: { version: SESSION_RECORD_VERSION, accountId, id: session.id, updatedAt: now,
      title: String(session.creativeBrief?.rawPrompt || session.creativeBrief?.masterPrompt || session.wizardDraft || 'Creative session').slice(0, 160), data },
    assets
  };
}

export function restoreSessionRecord(record, assets, accountId) {
  if (!record || record.accountId !== accountId) throw new Error('This saved session belongs to another account.');
  if (record.version !== SESSION_RECORD_VERSION) throw new Error('This saved session needs a newer version of Vibe.');
  const blobs = new Map(assets.filter((asset) => asset.accountId === accountId && asset.sessionId === record.id)
    .map((asset) => [asset.id, asset.blob]));
  function hydrate(value) {
    if (Array.isArray(value)) return value.map(hydrate);
    if (value && Object.getPrototypeOf(value) === Object.prototype) {
      if (Object.keys(value).length === 1 && typeof value.$vibeAsset === 'string') {
        if (!blobs.has(value.$vibeAsset)) throw new Error('An image from this saved session is missing. Keep this tab open and retry saving.');
        return blobs.get(value.$vibeAsset);
      }
      return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, hydrate(item)]));
    }
    return value;
  }
  return hydrate(record.data);
}

export function knownProjectIds(run) {
  return [...new Set(Object.values(run?.results || {}).map((result) => result?.projectId).filter(Boolean))];
}

export function storageErrorMessage(error) {
  if (error?.name === 'QuotaExceededError' || error?.inner?.name === 'QuotaExceededError') {
    return 'Device storage is full. Download your work, then remove saved sessions or videos to make room.';
  }
  return error?.message || 'This device could not save your session. Keep this tab open and try again.';
}

export function attachRetainedImages(snapshot, lookupBlob) {
  const attach = (image) => {
    if (image?.url && !image.blob) {
      const blob = lookupBlob(image.url);
      if (blob) image.blob = blob;
    }
  };
  for (const branch of snapshot.session?.branches || []) {
    for (const iteration of branch.iterations || []) {
      for (const batch of iteration.batches || []) for (const image of batch || []) attach(image);
    }
  }
  for (const result of Object.values(snapshot.generationRun?.results || {})) attach(result);
}

/** Fetch only known completed images, never previews or account history. */
export async function retainCompletedImages(snapshot, fetchBlob) {
  const images = new Map();
  const visit = (image) => {
    if (image?.url && !image.blob && !image.url.startsWith('blob:') &&
      (image.status === 'done' || image.status === 'completed')) {
      if (!images.has(image.url)) images.set(image.url, []);
      images.get(image.url).push(image);
    }
  };
  for (const branch of snapshot.session?.branches || []) {
    for (const iteration of branch.iterations || []) {
      for (const batch of iteration.batches || []) for (const image of batch || []) visit(image);
    }
  }
  for (const result of Object.values(snapshot.generationRun?.results || {})) visit(result);
  // Keep download work bounded and deduplicate URLs shared by branches.
  const entries = [...images.entries()];
  let next = 0;
  const worker = async () => {
    while (next < entries.length) {
      const [url, owners] = entries[next++];
      const blob = await fetchBlob(url);
      for (const image of owners) image.blob = blob;
    }
  };
  await Promise.all([worker(), worker()]);
  return snapshot;
}

export function restoreMediaUrls(snapshot, createUrl) {
  const urls = new Map();
  const restore = (image) => {
    if (!(image?.blob instanceof Blob)) return;
    if (!urls.has(image.blob)) urls.set(image.blob, createUrl(image.blob));
    image.url = urls.get(image.blob);
  };
  for (const branch of snapshot.session?.branches || []) {
    for (const iteration of branch.iterations || []) {
      for (const batch of iteration.batches || []) for (const image of batch || []) restore(image);
    }
  }
  for (const result of Object.values(snapshot.generationRun?.results || {})) restore(result);
  if (snapshot.session?.finalImageBlob instanceof Blob) {
    const blob = snapshot.session.finalImageBlob;
    if (!urls.has(blob)) urls.set(blob, createUrl(blob));
    snapshot.session.finalImageUrl = urls.get(blob);
  }
  return [...urls.values()];
}
